import React from 'react';
import { FaLocationArrow, FaPlus, FaMinus } from 'react-icons/fa';
import { BackIcon, Spinner, formatPrice } from './catalogueHelpers';

const CheckoutView = ({
  checkoutItems, checkoutTotal, checkoutCount,
  buyNowItems, setBuyNowItems,
  addToCart, removeFromCart,
  catalogue, accentColor,
  selectedCountryCode, setSelectedCountryCode, countryOptions,
  checkoutForm, setCheckoutForm, checkoutErrors, setCheckoutErrors,
  hasPrefill, clearPrefill,
  isSubmitting, handleSubmitOrder,
  setView, setShowLocationPicker,
  estimation, isEstimating,
}) => {
  const codByDefault = catalogue?.settings?.codByDefault !== false;

  const handleCheckoutChange = (e) => {
    const { name, value } = e.target;
    setCheckoutForm(prev => ({ ...prev, [name]: value }));
    if (checkoutErrors[name]) setCheckoutErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white px-4 sticky top-0 z-10">
        <div className="max-w-xl mx-auto flex items-center gap-1 py-4 border-b border-gray-200">
          <button onClick={() => { const wasBuyNow = buyNowItems !== null; setBuyNowItems(null); setView(wasBuyNow ? 'product' : 'catalogue'); }} className="p-2 -ml-2 text-gray-500 hover:text-gray-900 shrink-0">
            <BackIcon />
          </button>
          <h1 className="text-lg font-bold text-gray-900 truncate">Finaliser la commande</h1>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
        {/* Cart summary */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <h2 className="font-semibold text-gray-900">Votre panier</h2>
          {checkoutItems.map(item => (
            <div key={item.product.id} className="flex items-center gap-3">
              {item.product.images?.[0]?.url ? (
                <img src={item.product.images[0].url} alt={item.product.name} className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#d1d5db" strokeWidth="1.5"/>
                    <circle cx="8.5" cy="8.5" r="1.5" stroke="#d1d5db" strokeWidth="1.5"/>
                    <path d="M3 15l5-5 4 4 3-3 6 6" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item.product.name}</p>
                {item.variant && <p className="text-xs text-gray-400">{item.variant.name}</p>}
                {(item.options ?? []).length > 0 && (
                  <p className="text-xs text-gray-400 truncate">{item.options.map(o => (o.qty ?? 1) > 1 ? `×${o.qty} ${o.name}` : o.name).join(' • ')}</p>
                )}
                {(() => {
                  const base = item.variant?.price ?? item.product.price;
                  if (base == null) return null;
                  const optionsTotal = (item.options ?? []).reduce((s, o) => s + (o.price ?? 0) * (o.qty ?? 1), 0);
                  return <p className="text-xs text-gray-500">{formatPrice(base + optionsTotal)}</p>;
                })()}
              </div>
              {!buyNowItems ? (
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => removeFromCart(item.product)} className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-300">
                    <FaMinus className="w-3 h-3" />
                  </button>
                  <span className="w-5 text-center text-sm font-semibold">{item.quantity}</span>
                  <button onClick={() => addToCart(item.product)} className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-300">
                    <FaPlus className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <span className="text-sm font-semibold text-gray-700 shrink-0">x{item.quantity}</span>
              )}
            </div>
          ))}
        </div>

        {/* Contact form */}
        <form onSubmit={handleSubmitOrder} className="space-y-4" noValidate>
          {hasPrefill && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-100 border border-gray-200 text-sm">
              <span className="text-gray-700">Vos informations ont été pré-remplies depuis votre dernière commande.</span>
              <button
                type="button"
                onClick={clearPrefill}
                className="text-gray-900 font-medium hover:underline shrink-0"
              >
                Effacer
              </button>
            </div>
          )}
          <h2 className="text-lg font-semibold text-gray-900">Informations de contact</h2>

          <div>
            <input
              type="text"
              name="customer_name"
              value={checkoutForm.customer_name}
              onChange={handleCheckoutChange}
              placeholder="Votre nom complet"
              className={`w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:bg-white transition-all ${checkoutErrors.customer_name ? 'focus:ring-red-500' : 'focus:ring-black'}`}
            />
            {checkoutErrors.customer_name && <p className="mt-1 text-sm text-red-600">{checkoutErrors.customer_name}</p>}
          </div>

          <div>
            <div className="flex gap-3">
              <div className="w-20 relative">
                <select
                  value={selectedCountryCode}
                  onChange={e => setSelectedCountryCode(e.target.value)}
                  className="w-full bg-gray-100 rounded-lg px-3 py-3 text-transparent focus:outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all appearance-none cursor-pointer"
                >
                  {countryOptions.map(opt => (
                    <option key={opt.code} value={opt.code}>{opt.label} ({opt.code})</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-900 font-medium">{selectedCountryCode}</div>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">▾</div>
              </div>
              <input
                type="tel"
                name="phone"
                value={checkoutForm.phone}
                onChange={e => {
                  setCheckoutForm(prev => ({ ...prev, phone: e.target.value.replace(/[^0-9+\s]/g, '') }));
                  if (checkoutErrors.phone) setCheckoutErrors(prev => ({ ...prev, phone: '' }));
                }}
                placeholder="Numéro de téléphone"
                className={`flex-1 bg-gray-100 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:bg-white transition-all ${checkoutErrors.phone ? 'focus:ring-red-500' : 'focus:ring-black'}`}
              />
            </div>
            {checkoutErrors.phone && <p className="mt-1 text-sm text-red-600">{checkoutErrors.phone}</p>}
          </div>

          <h2 className="text-lg font-semibold text-gray-900 pt-2">Adresse de livraison</h2>

          <div>
            <div className="relative">
              <input
                type="text"
                value={checkoutForm.address || ''}
                onClick={() => { setShowLocationPicker(true); }}
                placeholder="Sélectionner une adresse"
                className={`w-full bg-gray-100 rounded-lg pl-4 pr-12 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:bg-white transition-all cursor-pointer ${checkoutErrors.address ? 'focus:ring-red-500' : 'focus:ring-black'}`}
                readOnly
              />
              <button type="button" onClick={() => setShowLocationPicker(true)} className="absolute right-4 top-1/2 -translate-y-1/2 transition-transform hover:scale-110">
                <FaLocationArrow className="w-4 h-4 text-gray-800" />
              </button>
            </div>
            {checkoutErrors.address && <p className="mt-1 text-sm text-red-600">{checkoutErrors.address}</p>}
          </div>

          <input
            type="text"
            name="instructions"
            value={checkoutForm.instructions}
            onChange={handleCheckoutChange}
            placeholder="Plus de détails sur l'adresse (facultatif)"
            className="w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black focus:bg-white transition-all"
          />

          {/* Order summary */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Articles ({checkoutCount})</span>
                <span className="font-medium text-gray-900">{formatPrice(checkoutTotal)}</span>
              </div>
              {catalogue?.settings?.estimationEnabled && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Livraison</span>
                  {isEstimating
                    ? <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
                    : estimation?.tariff != null
                      ? <span className="font-medium text-gray-900">{formatPrice(estimation.tariff)}</span>
                      : <span className="text-gray-400 italic">{checkoutForm.address ? '-' : 'Sélectionnez une adresse'}</span>
                  }
                </div>
              )}
              <div className="flex justify-between pt-2.5 border-t border-gray-200">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-bold text-gray-900">
                  {catalogue?.settings?.estimationEnabled && estimation?.tariff != null
                    ? formatPrice(checkoutTotal + estimation.tariff)
                    : formatPrice(checkoutTotal)
                  }
                </span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full text-white font-bold py-4 rounded-lg transition-all flex items-center justify-center active:scale-[0.98]"
            style={{ backgroundColor: accentColor || '#F89133' }}
          >
            {isSubmitting ? <Spinner size="sm" /> : <span>Confirmer la commande</span>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CheckoutView;
